/**
 * Cart Controller - Centralized cart management
 * REFACTORED: Uses shared services (VariantSelector, CartManager)
 * 
 * Handles cart state, date synchronization, variant updates, and validation
 */

console.log('📦 js-cart-controller.js loaded');

class CartController {
  constructor() {
    this.cart = null;
    this.rentalStartDate = null;
    this.rentalEndDate = null;
    this.maxRentalDays = null;
    this.hasRentalItems = false; // Track if cart has rental items
    this.init();
  }

  /**
   * Initialize cart controller
   */
  async init() {
    console.log('🛒 Cart Controller initializing...');
    
    try {
      // Load initial cart state
      console.log('🔄 Step 1/4: Loading cart...');
      await this.loadCart();
      
      // Cache DOM elements
      console.log('🔄 Step 2/4: Caching DOM elements...');
      this.cacheElements();
      
      // Bind event listeners
      console.log('🔄 Step 3/4: Binding event listeners...');
      this.bindEvents();
      
      // Determine max rental days from cart items
      console.log('🔄 Step 4/4: Calculating max rental days...');
      this.calculateMaxRentalDays();
      
      console.log('✅ Cart Controller initialized successfully');
    } catch (error) {
      console.error('❌ Cart Controller initialization failed:', error);
      console.error('Stack trace:', error.stack);
      throw error; // Re-throw to be caught by the DOMContentLoaded handler
    }
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    // Date inputs
    this.rentalStartDateInput = document.getElementById('rental-start-date');
    this.rentalEndDateInput = document.getElementById('rental-end-date');
    
    // Buttons
    this.checkoutButton = document.getElementById('checkout-button');
    this.clearCartButton = document.getElementById('clear-cart-button');
    
    // Form
    this.cartForm = document.getElementById('cart-form');
    
    // Data download radios
    this.dataDownloadRadios = document.querySelectorAll('input[name="attributes[Needs Data Download]"]');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Date change listeners
    if (this.rentalStartDateInput) {
      this.rentalStartDateInput.addEventListener('change', () => this.handleDateChange());
    }
    
    if (this.rentalEndDateInput) {
      this.rentalEndDateInput.addEventListener('change', () => this.handleDateChange());
    }

    // Checkout validation
    if (this.checkoutButton && this.cartForm) {
      this.checkoutButton.addEventListener('click', (e) => this.validateCheckout(e));
    }

    // Clear cart
    if (this.clearCartButton) {
      this.clearCartButton.addEventListener('click', () => this.clearCart());
    }

    // Listen for rental dates changed event from product pages
    document.addEventListener('rentalDatesChanged', (e) => {
      console.log('📅 Rental dates changed event received:', e.detail);
      this.loadCart();
    });

    // Auto-save cart attributes on change
    this.bindAutoSaveAttributes();
    
    // Subscribe to EventBus events
    this.subscribeToEvents();
  }
  
  /**
   * Subscribe to EventBus events
   */
  subscribeToEvents() {
    if (!window.eventBus) {
      console.warn('⚠️ EventBus not available in CartController');
      return;
    }
    
    console.log('📡 CartController: Subscribing to events');
    
    // Listen for DATES_CHANGED event
    window.eventBus.on('DATES_CHANGED', async ({ startDate, endDate, days }) => {
      console.log('📅 CartController: DATES_CHANGED event received', { startDate, endDate, days });
      
      try {
        // Show loading message in console (page already has spinner from date selector)
        console.log('🔄 Step 1/3: Reloading cart data...');
        
        // Reload cart data to get latest state
        await this.loadCart();
        
        console.log('🔄 Step 2/3: Swapping cart item variants to match new dates...');
        
        // Update all rental items to match the new dates
        await this.updateRentalItemsForDates(startDate, endDate, days);
        
        console.log('✅ Step 3/3: Variant swaps complete - reloading page...');
        
        // Reload page to show updated cart
        window.location.reload();
      } catch (error) {
        console.error('❌ Error during cart update:', error);
        
        // Remove loading states
        const cartMain = document.querySelector('.cart-main') || document.querySelector('main');
        if (cartMain) {
          cartMain.classList.remove('rr-loading', 'rr-loading--spinner');
        }
        
        alert('There was an error updating your cart. Please refresh the page and try again.');
      }
    });
    
    // Listen for CART_UPDATED event
    window.eventBus.on('CART_UPDATED', async ({ cart }) => {
      console.log('🛒 CartController: CART_UPDATED event received');
      this.cart = cart;
      this.rentalStartDate = cart.attributes?.['Rental Start Date'];
      this.rentalEndDate = cart.attributes?.['Rental End Date'];
      
      // Detect and update rental status
      await this.updateRentalStatus(cart);
    });
  }

  /**
   * Load cart data from Shopify
   * REFACTORED: Uses CartManager service if available
   */
  async loadCart() {
    console.log('🔄 Loading cart data...');
    try {
      // Use CartManager service if available
      this.cart = window.CartManager 
        ? await CartManager.getCart()
        : await (await fetch('/cart.js')).json();
      
      this.rentalStartDate = this.cart.attributes?.['Rental Start Date'];
      this.rentalEndDate = this.cart.attributes?.['Rental End Date'];
      
      // Detect and update rental status on initial load
      await this.updateRentalStatus(this.cart);
      
      console.log('🛒 Cart loaded:', {
        items: this.cart.item_count,
        hasRentalItems: this.hasRentalItems,
        startDate: this.rentalStartDate,
        endDate: this.rentalEndDate
      });
      
      // Empty carts can retain rental dates from a previous session and still
      // render them in the date selector. Validate those cached dates as well
      // as dates attached to an active rental cart.
      const shouldValidateRentalDates =
        this.hasRentalItems || this.cart?.item_count === 0;

      if (
        shouldValidateRentalDates &&
        this.rentalStartDate &&
        this.rentalEndDate
      ) {
        await this.validateAndHandleRentalDates();
      }

      await this.syncShortTurnaroundAttribute();
      
      return this.cart;
    } catch (error) {
      console.error('Error loading cart:', error);
      return null;
    }
  }

  /**
   * Validate rental dates and handle invalid dates
   * Called when cart loads to check for stale or invalid dates
   * Shows non-blocking offcanvas warning
   */
  async validateAndHandleRentalDates() {
    console.log('🔍 Validating rental dates...');
    
    // Check if DateValidationService is available
    if (!window.DateValidationService) {
      console.warn('⚠️ DateValidationService not available - skipping validation');
      return;
    }
    
    // Validate dates
    const validation = window.DateValidationService.validate(
      this.rentalStartDate,
      this.rentalEndDate,
      this.maxRentalDays
    );
    
    console.log('📋 Validation result:', validation);
    
    // If dates are valid, nothing to do
    if (validation.valid) {
      console.log('✅ Rental dates are valid');
      return;
    }
    
    // Dates are invalid - show toast warning (non-blocking)
    console.warn('⚠️ Invalid rental dates detected:', validation.errors);
    
    // Emit event for DateValidationUI to handle
    if (window.eventBus) {
      window.eventBus.emit('VALIDATION_FAILED', {
        validation,
        context: 'cart',
        blocking: false
      });
    }
  }

  /**
   * Check if cart has any items
   * 
   * @param {object} cart - Shopify cart object
   * @returns {boolean} True if cart exists and has items
   */
  cartHasItems(cart) {
    if (!cart) {
      console.log('🔄 Cart does not exist');
      return false;
    }
    if (!cart.items) {
      console.log('🔄 Cart items do not exist');
      return false;
    }
    if (cart.items.length === 0) {
      console.log('🔄 Cart items are empty');
      return false;
    }
    return true;
  }

  /**
   * Detect if cart contains any rental items
   * An item is considered a rental item if variant option1 = "Rent"
   * 
   * @param {object} cart - Shopify cart object
   * @returns {boolean} True if cart has at least one rental item
   */
  detectRentalItems(cart) {
    console.log('🔍 Detecting rental items in cart...');

    if (!this.cartHasItems(cart)) {
      console.log('🔄 Cart does not have items');
      return false;
    }

    const rentalItems = cart.items.filter(item => {
      console.log('🔍 Checking item:', {
        title: item.title || item.product_title,
        variant_options: item.variant_options,
        option1_raw: item.variant_options?.[0],
        option1_lowercase: item.variant_options?.[0]?.toLowerCase()
      });
      
      const option1 = item.variant_options?.[0]?.toLowerCase();
      const isRental = option1 === 'rent';
      
      console.log(`   → Is rental: ${isRental} (option1: "${option1}")`);
      
      return isRental;
    });

    const hasRentalItems = rentalItems.length > 0;
    console.log('✅ Final result - Has rental items:', hasRentalItems, `(${rentalItems.length} rental items found)`);
    
    return hasRentalItems;
  }

  /**
   * Update cart rental status attribute
   * Only updates if status has changed to avoid unnecessary API calls
   * Emits CART_RENTAL_STATUS_CHANGED event if status changed
   * 
   * @param {object} cart - Shopify cart object
   */
  async updateRentalStatus(cart) {
    console.log('🔄 Updating cart rental status...');
    try {
      const hasRentalItems = this.detectRentalItems(cart);
      const currentStatus = cart.attributes?.['Has Rental Items'];
      const newStatus = hasRentalItems ? 'true' : 'false';
      
      // Only update if status changed
      if (currentStatus !== newStatus) {
        console.log(`🔄 Cart rental status changing: ${currentStatus} → ${newStatus}`);
        
        // Update cart attribute
        if (window.CartManager) {
          await CartManager.updateAttributes({
            'Has Rental Items': newStatus
          });
        }
        
        // Update local state
        this.hasRentalItems = hasRentalItems;
        
        // Emit status change event
        if (window.eventBus) {
          window.eventBus.emit('CART_RENTAL_STATUS_CHANGED', {
            hasRentalItems,
            cart
          });
        }
      } else {
        // Status hasn't changed, just update local state
        this.hasRentalItems = hasRentalItems;
      }
    } catch (error) {
      console.error('❌ Error updating rental status:', error);
    }
  }

  /**
   * Calculate max rental days based on cart items' variant options
   */
  async calculateMaxRentalDays() {
    if (!this.cartHasItems(this.cart)) {
      this.maxRentalDays = null;
      return;
    }

    let maxDays = null;

    // Check each item's product for rental duration options
    for (const item of this.cart.items) {
      try {
        // Fetch product data
        const response = await fetch(`/products/${item.handle}.js`);
        const productData = await response.json();

        // Find all rental variants and extract day counts
        const rentalVariants = productData.variants
          .filter(v => v.option1?.toLowerCase() === 'rent')
          .map(v => {
            const match = v.option2?.match(/^(\d+)\s+Day/);
            return match ? parseInt(match[1]) : 0;
          })
          .filter(days => days > 0);

        if (rentalVariants.length > 0) {
          const productMaxDays = Math.max(...rentalVariants);
          
          // Use the minimum max across all products (most restrictive)
          if (maxDays === null || productMaxDays < maxDays) {
            maxDays = productMaxDays;
          }
        }
      } catch (error) {
        console.error(`Error fetching product data for ${item.handle}:`, error);
      }
    }

    this.maxRentalDays = maxDays;
    console.log('📊 Max rental days determined:', this.maxRentalDays);

    // Dispatch event for date picker to listen to
    document.dispatchEvent(new CustomEvent('cart:maxDaysCalculated', {
      detail: { maxDays: this.maxRentalDays }
    }));

    return this.maxRentalDays;
  }

  /**
   * Handle date changes
   * REFACTORED: Uses CartManager and emits DATES_CHANGED event
   */
  async handleDateChange() {
    const startDate = this.rentalStartDateInput?.value;
    const endDate = this.rentalEndDateInput?.value;

    if (!startDate || !endDate) return;

    console.log('📅 Dates changed:', { startDate, endDate });

    // Calculate days
    const days = window.VariantSelector 
      ? VariantSelector.calculateDays(startDate, endDate)
      : this.calculateRentalDays(startDate, endDate);
    const attributes = {
      'Rental Start Date': startDate,
      'Rental End Date': endDate,
      'Rental Duration (Days)': days.toString()
    };
    const shortTurnaround = this.getShortTurnaroundAttribute(startDate);

    if (shortTurnaround !== null) {
      attributes['Short Turnaround Rental'] = shortTurnaround;
    }

    // Update cart attributes using CartManager service
    if (window.CartManager) {
      await CartManager.updateAttributes(attributes);
    } else {
      // Fallback
      await this.updateCartAttributes(attributes);
    }

    // Emit DATES_CHANGED event
    // The EventBus listener will handle variant swapping + reload
    if (window.eventBus) {
      console.log('📢 Emitting DATES_CHANGED event - EventBus will handle variant swap + reload');
      window.eventBus.emit('DATES_CHANGED', {
        startDate,
        endDate,
        days
      });
    } else {
      // Fallback if EventBus not available
      console.log('⚠️ EventBus not available - handling variant swap directly');
      await this.updateRentalItemsForDates(startDate, endDate, days);
      window.location.reload();
    }
  }

  /**
   * Calculate rental days (inclusive)
   * DEPRECATED: Use VariantSelector.calculateDays() instead
   * Kept for backwards compatibility only
   */
  calculateRentalDays(startDate, endDate) {
    // Use service if available
    if (window.VariantSelector) {
      return VariantSelector.calculateDays(startDate, endDate);
    }
    
    // Fallback implementation
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  }

  /**
   * Get the serialized short-turnaround cart attribute for a rental start date.
   */
  getShortTurnaroundAttribute(startDate) {
    if (
      typeof window.DateValidationService?.isEarliestAllowedStartDate !== 'function'
    ) {
      return null;
    }

    return startDate &&
      window.DateValidationService.isEarliestAllowedStartDate(startDate)
      ? 'Yes'
      : '';
  }

  /**
   * Keep existing carts in sync when they load with rental dates already selected.
   */
  async syncShortTurnaroundAttribute() {
    const shortTurnaround = this.getShortTurnaroundAttribute(this.rentalStartDate);
    if (shortTurnaround === null) return;

    const currentValue = this.cart?.attributes?.['Short Turnaround Rental'] || '';
    if (currentValue === shortTurnaround) return;

    await this.updateCartAttributes({
      'Short Turnaround Rental': shortTurnaround
    });
  }

  /**
   * Update cart attributes
   * REFACTORED: Uses CartManager service if available
   */
  async updateCartAttributes(attributes) {
    try {
      // Use CartManager service if available
      const data = window.CartManager 
        ? await CartManager.updateAttributes(attributes)
        : await (async () => {
            const response = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes })
            });
            return await response.json();
          })();
      
      console.log('✅ Cart attributes updated:', attributes);
      
      // Update local cart reference
      this.cart = data;
      
      return data;
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      return null;
    }
  }

  /**
   * Update rental items to match new dates
   * REFACTORED: Uses VariantSelector service
   */
  async updateRentalItemsForDates(startDate, endDate, days) {
    if (!this.cartHasItems(this.cart)) {
      console.log('⚠️ No cart or cart items to update');
      return;
    }

    console.log(`🔄 Updating ${this.cart.items.length} cart items for ${days} days`);

    for (const item of this.cart.items) {
      try {
        // Only update rental items
        const isRental = item.variant_title?.includes('Rent');
        if (!isRental) {
          console.log(`⏭️ Skipping ${item.product_title} (not a rental item)`);
          continue;
        }

        console.log(`🔍 Checking ${item.product_title} (current: ${item.variant_title})`);

        // Use VariantSelector service if available
        let matchingVariant;
        
        if (window.VariantSelector) {
          matchingVariant = await VariantSelector.selectVariantForDates(
            item.handle,
            startDate,
            endDate
          );
        } else {
          // Fallback to old logic
          const response = await fetch(`/products/${item.handle}.js`);
          const productData = await response.json();
          const targetVariantName = `Rent / ${days} Day`;
          matchingVariant = productData.variants.find(v => v.title === targetVariantName);
        }

        // Update if different variant
        if (matchingVariant && matchingVariant.id !== item.variant_id) {
          console.log(`🔄 Swapping ${item.product_title} from ${item.variant_title} to ${matchingVariant.title}`);
          await this.swapCartItemVariant(item, matchingVariant, days);
        } else if (matchingVariant) {
          console.log(`✅ ${item.product_title} already has correct variant`);
        } else {
          console.warn(`⚠️ No matching variant found for ${item.product_title}`);
        }
      } catch (error) {
        console.error(`❌ Error updating ${item.product_title}:`, error);
      }
    }
    
    console.log('✅ All cart items updated');
  }

  /**
   * Swap cart item to different variant
   * REFACTORED: Uses CartManager service if available
   */
  async swapCartItemVariant(item, newVariant, days) {
    try {
      // Prepare properties
      const properties = {
        'Rental Start Date': this.rentalStartDate,
        'Rental End Date': this.rentalEndDate,
        'Rental Duration (Days)': days
      };

      // Preserve other properties
      if (item.properties) {
        for (const [key, value] of Object.entries(item.properties)) {
          if (!key.startsWith('Rental') && !key.startsWith('_')) {
            properties[key] = value;
          }
        }
      }

      // Use CartManager service if available
      if (window.CartManager) {
        await CartManager.swapItemVariant(
          item.key,
          newVariant.id,
          item.quantity,
          properties
        );
      } else {
        // Fallback to direct fetch
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: newVariant.id,
            quantity: item.quantity,
            properties
          })
        });
      }

      console.log(`✅ Swapped ${item.product_title} to ${newVariant.title}`);
    } catch (error) {
      console.error('Error swapping variant:', error);
    }
  }

  /**
   * Validate checkout
   * Skips rental-specific validations for purchase-only carts
   */
  validateCheckout(e) {
    let isValid = true;
    let errorMessage = '';

    const cartItemCount = this.cart?.item_count || 0;

    // Validate rental-specific fields if:
    // 1. Cart is empty (treat as rental until items are added)
    // 2. Cart has rental items
    if (cartItemCount === 0 || this.hasRentalItems) {
      // Validate rental dates exist
      if (!this.rentalStartDate || !this.rentalEndDate) {
        isValid = false;
        errorMessage = 'Please select rental start and end dates.';
        const jobDetailsSection = document.querySelector('[data-section="job-details"]');
        if (jobDetailsSection) {
          jobDetailsSection.classList.add('open');
        }
      } else if (window.DateValidationService) {
        // Validate rental dates are valid
        const validation = window.DateValidationService.validate(
          this.rentalStartDate,
          this.rentalEndDate,
          this.maxRentalDays
        );
        
        if (!validation.valid) {
          isValid = false;
          // Show blocking modal via event
          e.preventDefault();
          
          if (window.eventBus) {
            window.eventBus.emit('VALIDATION_FAILED', {
              validation,
              context: 'checkout',
              blocking: true
            });
          }
          return false;
        }
      }

      // Validate data download selection
      let hasDataDownloadSelection = false;
      if (this.dataDownloadRadios.length > 0) {
        this.dataDownloadRadios.forEach(radio => {
          if (radio.checked) {
            hasDataDownloadSelection = true;
          }
        });
      }

      if (!hasDataDownloadSelection) {
        isValid = false;
        errorMessage = 'Please select whether you need data download service.';
        const dataDownloadField = document.getElementById('data-download-field');
        if (dataDownloadField) {
          dataDownloadField.classList.add('error');
        }
      }
    } else {
      console.log('ℹ️ Purchase-only cart - skipping rental validations');
    }

    // Prevent checkout if invalid
    if (!isValid) {
      e.preventDefault();
      alert(errorMessage);
      return false;
    }

    return true;
  }

  /**
   * Clear cart completely (items + attributes)
   */
  async clearCart() {
    const confirmed = confirm('Are you sure you want to clear your entire cart? This action cannot be undone.');

    if (!confirmed) return;

    const button = this.clearCartButton;
    if (button) {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.innerHTML = '<span>Clearing...</span>';
    }

    try {
      // Clear all cart items first
      console.log('🧹 Step 1/2: Clearing cart items...');
      const clearResponse = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!clearResponse.ok) {
        throw new Error(`Clear cart failed: ${clearResponse.status} ${clearResponse.statusText}`);
      }
      
      const clearData = await clearResponse.json();
      console.log('✅ Cart items cleared:', clearData);
      console.log('   Item count:', clearData.item_count);
      console.log('   Attributes before clear:', clearData.attributes);

      // Then clear all cart attributes (needs to be after clearing items)
      console.log('🧹 Step 2/2: Clearing cart attributes...');
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Rental Start Date': '',
            'Rental End Date': '',
            'Rental Duration (Days)': '',
            'Has Rental Items': '',
            'Short Turnaround Rental': '',
            'PO Reference': '',
            'Accounts Payable Email': '',
            'Needs Data Download': '',
            'Application': '',
            'Application Details': '',
            'Industry / Project': '',
            'Delivery Instructions': '',
            'Dietary Restrictions': ''
          }
        })
      });
      
      if (!updateResponse.ok) {
        throw new Error(`Update attributes failed: ${updateResponse.status} ${updateResponse.statusText}`);
      }
      
      const updateData = await updateResponse.json();
      console.log('✅ Cart attributes cleared:', updateData);
      console.log('   Attributes after clear:', updateData.attributes);
      
      // Log the final comparison
      console.log('📊 Clear Cart Summary:');
      console.log('   Before: Has attributes?', Object.keys(clearData.attributes || {}).length > 0);
      console.log('   After: Has attributes?', Object.keys(updateData.attributes || {}).length > 0);
      console.log('   Final attribute keys:', Object.keys(updateData.attributes || {}));

      console.log('✅ Cart and dates cleared - reloading page...');
      window.location.reload();
    } catch (error) {
      console.error('❌ Error clearing cart:', error);
      alert('Failed to clear cart. Please try again.');
      window.location.reload();
    }
  }

  /**
   * Auto-save cart attributes on input change
   */
  bindAutoSaveAttributes() {
    const attributeInputs = document.querySelectorAll('[data-cart-attribute]');
    
    attributeInputs.forEach(input => {
      // Skip inputs without a name attribute (handled by dedicated logic)
      if (!input.name || !input.name.includes('attributes[')) {
        return;
      }
      
      const saveAttribute = async () => {
        const name = input.name.replace('attributes[', '').replace(']', '');
        const value = input.value;
        
        await this.updateCartAttributes({
          [name]: value
        });
      };

      // Save on change for most inputs
      if (input.type === 'radio' || input.type === 'checkbox') {
        input.addEventListener('change', saveAttribute);
      } else {
        // Debounce text inputs
        let timeout;
        input.addEventListener('input', () => {
          clearTimeout(timeout);
          timeout = setTimeout(saveAttribute, 500);
        });
      }
    });
  }

  /**
   * Get max rental days for date picker restriction
   */
  getMaxRentalDays() {
    return this.maxRentalDays;
  }
}

// Initialize cart controller when DOM is ready
let cartController;

document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 Attempting to initialize CartController...');
  
  try {
    cartController = new CartController();
    
    // Make available globally for other scripts
    window.cartController = cartController;
    
    console.log('✅ CartController successfully assigned to window.cartController');
  } catch (error) {
    console.error('❌ Failed to initialize CartController:', error);
    console.error('Stack trace:', error.stack);
  }
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartController;
}
